import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, Box, Paper, Divider } from '@mui/material';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import SearchField from 'src/shared/components/form/searchField';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { GroupMessageEmailGrid } from 'src/modules/groupMessageEmail/components/groupMessageEmailGrid';
import MemberGroupEmailActionButtons from 'src/modules/groupMessageEmail/components/groupMessageEmailActionButtons';
import { LocalStorageKeys } from 'src/shared/models/enums/localStorageKeys.enum';
import { useLocalStorage } from 'src/shared/hooks/useLocalStorage';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { PageFilterSection } from 'src/shared/components/organisms/pageFilterSection';
import { GroupMessageEmailSearchFilter } from '../models/groupMessageEmailSearchFilter';
import { GroupMessageEmailTabs } from '../components/groupMessageEmailTabs';
import { EmailTemplatePickerModal } from '../components/emailTemplatePickerModal';

export const GroupMessageEmailOverviewPage = () => {
  const isMounted = useIsMounted();
  const [, setLoading] = useState<boolean>(false);
  const { t } = useTranslation(['groupMessage']);
  const [tempSearchString, setTempSearchString] = useState<string | undefined>(undefined);
  const [searchInitialized, setSearchInitialized] = useState<boolean>(false);
  const [openEmailTemplatePickerModal, setOpenEmailTemplatePickerModal] = useState<boolean>(false);
  const [groupMessageEmailSearchFilter, setGroupMessageEmailSearchFilter] = useLocalStorage(
    LocalStorageKeys.GroupMessageEmailSearchFilter,
    {} as unknown as GroupMessageEmailSearchFilter,
  );

  const searchFilter: GroupMessageEmailSearchFilter = useMemo(() => {
    return {
      searchString: groupMessageEmailSearchFilter?.searchString,
      isSent: groupMessageEmailSearchFilter?.isSent,
      activeTab: groupMessageEmailSearchFilter?.activeTab,
      archivedMessages: groupMessageEmailSearchFilter?.archivedMessages,
    } as unknown as GroupMessageEmailSearchFilter;
  }, [
    groupMessageEmailSearchFilter?.activeTab,
    groupMessageEmailSearchFilter?.archivedMessages,
    groupMessageEmailSearchFilter?.isSent,
    groupMessageEmailSearchFilter?.searchString,
  ]);

  const loadingCallback = useCallback((isLoading: boolean) => {
    setLoading(isLoading);
  }, []);

  useEffect(() => {
    if (isMounted() && searchInitialized) {
      if (tempSearchString === '') {
        setGroupMessageEmailSearchFilter((prev) => ({ ...prev, ...{ searchString: '' } }));
      }
      if (tempSearchString && tempSearchString.length >= 2) {
        setGroupMessageEmailSearchFilter((prev) => ({ ...prev, ...{ searchString: tempSearchString } }));
      } else {
        setGroupMessageEmailSearchFilter((prev) => ({ ...prev, ...{ searchString: '' } }));
      }
    }
  }, [isMounted, searchInitialized, setGroupMessageEmailSearchFilter, tempSearchString]);

  return (
    <>
      <GridWrapper>
        <Grid item xs='auto'>
          <MainContentHeader title={t('groupMessage:overview.header')} />
        </Grid>
        <Grid item xs sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end' }}>
            <MemberGroupEmailActionButtons openTemplatePicker={setOpenEmailTemplatePickerModal} />
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Paper>
            <PageFilterSection
              leftContent={
                <SearchField
                  initialValue={searchFilter.searchString || ''}
                  onSearch={(newSearchString) => {
                    setSearchInitialized(true);
                    setTempSearchString(newSearchString);
                  }}
                />
              }
            />
            <GroupMessageEmailTabs
              initialValue={searchFilter.activeTab}
              callback={(newActiveTab, newIsSent, archivedMessages) => {
                setGroupMessageEmailSearchFilter((prev) => ({
                  ...prev,
                  ...{ activeTab: newActiveTab, isSent: newIsSent, archivedMessages },
                }));
              }}
            />
            <Divider />
            <GroupMessageEmailGrid loadingCallback={loadingCallback} searchFilter={searchFilter} />
          </Paper>
        </Grid>
      </GridWrapper>
      {openEmailTemplatePickerModal && <EmailTemplatePickerModal open={openEmailTemplatePickerModal} openModal={setOpenEmailTemplatePickerModal} />}
    </>
  );
};
